import { FunctionComponent, KeyboardEvent, MouseEvent } from "react";
import { useTranslation } from "react-i18next";
import { faStar as faStarOutline } from "@fortawesome/pro-regular-svg-icons";
import { faStar as faStarSolid } from "@fortawesome/pro-solid-svg-icons";

import { Icon } from "@/components/Icon/Icon";
import { SvgIcon } from "@/components/Icon/SvgIcon";
import { Tooltip } from "@/components/Tooltip/Tooltip";
import { classNames } from "@/utils/classNames";

import { CatalogIconVariant, CatalogTile, CatalogTileRecurrence } from "../config/tileCatalog";
import { displayRequirements } from "../utils/catalogFilters";
import { CatalogDisabledState } from "../utils/catalogAvailability";
import { RequirementBadge } from "./RequirementBadge";

export const iconVariantClasses: Record<CatalogIconVariant, string> = {
	[CatalogIconVariant.CSV]: "bg-status-success-light text-status-success",
	[CatalogIconVariant.SCRAPE]: "bg-status-running-light text-status-running",
	[CatalogIconVariant.LINKEDIN]: "bg-blue-action-200 text-blue-action",
	[CatalogIconVariant.DATABASE]: "bg-background-light text-text-meta",
	[CatalogIconVariant.MAPS]: "bg-tag-light text-tag-dark",
	[CatalogIconVariant.JOBS]: "bg-status-warning-light text-status-warning",
	[CatalogIconVariant.FUNDING]: "bg-green-200 text-status-success",
	[CatalogIconVariant.SEMANTIC]: "bg-purple-100 text-purple-800",
	[CatalogIconVariant.TRACKER]: "bg-tag text-tag-dark",
	[CatalogIconVariant.EMPTY]: "bg-background-light text-text-meta",
};

interface CatalogCardProps {
	tile: CatalogTile;
	disabledState: CatalogDisabledState;
	isHighlighted?: boolean;
	isContentTopAligned?: boolean;
	isCompact?: boolean;
	recommendationRank?: number;
	recommendationReason?: string;
	isFavorite?: boolean;
	isBusy?: boolean;
	onToggleFavorite?: (tile: CatalogTile) => void;
	onClick: (tile: CatalogTile) => void;
}

export const CatalogCard: FunctionComponent<CatalogCardProps> = ({
	tile,
	disabledState,
	isHighlighted = false,
	isContentTopAligned = false,
	isCompact = false,
	recommendationRank,
	recommendationReason,
	isFavorite = false,
	isBusy = false,
	onToggleFavorite,
	onClick,
}) => {
	const { t } = useTranslation();
	const isDisabled = disabledState.disabled || isBusy;
	const requirements = displayRequirements(tile);
	const getRecurrenceKey = (recurrence: CatalogTileRecurrence) =>
		recurrence === CatalogTileRecurrence.RECURRING ? "recurring" : "oneTime";

	const handleToggleFavorite = (event: MouseEvent | KeyboardEvent) => {
		event.preventDefault();
		event.stopPropagation();
		onToggleFavorite?.(tile);
	};

	const requirementBadges = requirements.length > 0 && (
		<div className={classNames("flex flex-wrap gap-1.5", isCompact ? "" : "justify-end")}>
			{requirements.map((requirement) => (
				<RequirementBadge key={requirement} requirement={requirement} />
			))}
		</div>
	);

	const favoriteStar = onToggleFavorite && (
		<span
			aria-label={t(
				isFavorite
					? "list.create.catalog.rail.removeFavorite"
					: "list.create.catalog.rail.addFavorite"
			)}
			aria-pressed={isFavorite}
			className={classNames(
				"flex h-6 w-6 shrink-0 items-center justify-center rounded-md transition",
				isCompact ? "absolute right-2 top-2" : "",
				isFavorite
					? "text-status-warning"
					: "text-text-disabled opacity-0 hover:bg-background-light hover:text-status-warning group-hover:opacity-100"
			)}
			role="button"
			tabIndex={0}
			onClick={handleToggleFavorite}
			onKeyDown={(event) => {
				if (event.key === "Enter" || event.key === " ") {
					handleToggleFavorite(event);
				}
			}}
		>
			<Icon className="h-4 w-4" icon={isFavorite ? faStarSolid : faStarOutline} />
		</span>
	);

	return (
		<Tooltip
			className="w-full !flex"
			content={disabledState.disabledMessageKey ? t(disabledState.disabledMessageKey) : undefined}
			disabled={!disabledState.disabledMessageKey}
		>
			<button
				className={classNames(
					"group flex w-full gap-4 rounded-[10px] border border-border-light bg-white p-4 text-left transition duration-150",
					isCompact ? "relative h-full items-start pr-9" : "",
					!isCompact && isContentTopAligned ? "items-start" : "",
					!isCompact && !isContentTopAligned ? "items-center" : "",
					"hover:border-blue-action hover:shadow-sm",
					isHighlighted ? "border-blue-action bg-selected-item shadow-sm" : "",
					isDisabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"
				)}
				disabled={isDisabled}
				type="button"
				onClick={() => onClick(tile)}
			>
				<div
					className={classNames(
						"relative flex h-10 w-10 shrink-0 items-center justify-center rounded-lg",
						iconVariantClasses[tile.iconVariant]
					)}
				>
					{recommendationRank && (
						<span className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full border-2 border-white bg-blue-action text-xxs font-bold text-white shadow-sm">
							{recommendationRank}
						</span>
					)}
					{tile.icon && <SvgIcon className="h-[20px] w-[20px]" svgIcon={tile.icon} />}
					{tile.secondIcon && <Icon className="h-[20px] w-[20px]" icon={tile.secondIcon} />}
				</div>

				<div className="min-w-0 flex-1">
					<div className="mb-1 flex flex-wrap items-center gap-2">
						<span className="text-sm font-semibold text-text">{tile.title}</span>
						{tile.recurrences.map((recurrence) => (
							<span
								key={recurrence}
								className={classNames(
									"rounded px-1.5 py-0.5 text-xxs font-bold uppercase tracking-wide",
									recurrence === CatalogTileRecurrence.RECURRING
										? "bg-blue-action-200 text-blue-action"
										: "bg-background-light text-text-meta"
								)}
							>
								{t(`list.create.catalog.recurrence.${getRecurrenceKey(recurrence)}`)}
							</span>
						))}
					</div>
					<p className="text-xs leading-5 text-text-meta">{tile.description}</p>
					{recommendationReason && (
						<p className="mt-2 rounded-lg bg-light-blue px-2 py-1.5 text-xs leading-5 text-text-meta">
							{recommendationReason}
						</p>
					)}
					{isCompact && requirementBadges && <div className="mt-2.5">{requirementBadges}</div>}
				</div>

				{isCompact
					? favoriteStar
					: (requirements.length > 0 || onToggleFavorite) && (
							<div className="flex shrink-0 items-center gap-2">
								{requirementBadges}
								{favoriteStar}
							</div>
						)}
			</button>
		</Tooltip>
	);
};
